import { AutoComplete, Input } from "antd";
import React, { useState, useEffect, useRef } from "react";

import "./stylesCustomAutoComplete.css";
import { SearchOutlined } from "@ant-design/icons";
import { AppStrings } from "../../../utils/constants/AppStrings";

function CustomAutoComplete({
  className = "",
  allOptions = [],
  placeholder = AppStrings.SEARCH,
  handleChange,
  clearOptions,
  selector = "game_id",
  bgColor = "transparent",
  value = "",
  onSearchChange, // New prop for external search callback
  virtual = true,
}) {
  const [options, setOptions] = useState(allOptions);
  const [inputValue, setInputValue] = useState("");
  
  // Track if user is actively searching (not just editing a selection)
  const isSearchingRef = useRef(false);
  // Track the last confirmed selection value
  const lastSelectionRef = useRef(value);

  // Sync inputValue with external value (for controlled component)
  // Only sync when value actually changes from parent (not during search)
  useEffect(() => {
    if (isSearchingRef.current) {
      // Don't reset input while user is searching
      return;
    }
    
    if (value) {
      // Find the label for the selected value
      const selectedOption = allOptions.find((opt) => opt[selector] === value);
      setInputValue(selectedOption?.label || value);
      lastSelectionRef.current = value;
    } else if (lastSelectionRef.current !== value) {
      // Only clear if the value actually changed from parent
      setInputValue("");
      lastSelectionRef.current = value;
    }
  }, [value, selector]); // Removed allOptions from deps to prevent reset on re-render

  const onSearch = (searchText) => {
    isSearchingRef.current = true;
    setInputValue(searchText);
    
    // Call external search callback if provided
    if (onSearchChange) {
      onSearchChange(searchText);
    }

    if (!searchText) {
      setOptions(allOptions);
      // Only clear selection when input is completely emptied
      if (lastSelectionRef.current) {
        clearOptions?.();
        lastSelectionRef.current = "";
      }
      isSearchingRef.current = false;
      return;
    }

    const filtered = allOptions.filter((option) => {
      const optValue = option?.value?.toString().toLowerCase() || "";
      const label = option?.label?.toString().toLowerCase() || "";
      const searchLower = searchText.toLowerCase();
      return optValue.includes(searchLower) || label.includes(searchLower);
    });
    setOptions(filtered);
  };

  const onSelect = (selectedValue) => {
    isSearchingRef.current = false;
    const selectedItem = allOptions.find(
      (option) => option.value === selectedValue
    );
    if (selectedItem) {
      setInputValue(selectedItem.label || selectedValue);
      lastSelectionRef.current = selectedItem[selector];
      handleChange?.(selectedItem[selector]); 
    }
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    
    // Only clear selection when input is completely emptied
    if (!newValue && lastSelectionRef.current) {
      clearOptions?.();
      lastSelectionRef.current = "";
      isSearchingRef.current = false;
    }
  };

  useEffect(() => {
    setOptions(allOptions);
  }, [allOptions]);

  return (
    <AutoComplete
      className="custom-autocomplete"
      options={options}
      value={inputValue}
      onSelect={onSelect}
      onSearch={onSearch}
      onChange={handleInputChange}
      placeholder={placeholder}
      virtual={virtual}
      style={{
        width: "100%",
        background: "transparent",
        height: "40px",
      }}
    >
      <Input
        style={{
          background: bgColor,
          height: "40px",
        }}
        prefix={<SearchOutlined style={{ fontSize: "18px", color: "#92929E" }} />}
      />
    </AutoComplete>
  );
}

export default CustomAutoComplete;
